import {PersonIcon} from '@primer/octicons-react'
import {Box, Text} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {AssignedEvent$key} from './__generated__/AssignedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'
import {Fragment} from 'react'
import {Assignee} from './Assignee'
import {RolledupAssignedEvent} from './RolledupAssignedEvent'

type AssignedEventProps = {
  queryRef: AssignedEvent$key
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  rollupGroup?: Record<string, Array<AssignedEvent$key | AssignedEvent$key>>
}

const AssignedEventFragment = graphql`
  fragment AssignedEvent on AssignedEvent {
    databaseId
    createdAt
    actor {
      ...TimelineRowEventActor
      login
    }
    assignee {
      ... on User {
        login
      }
      ... on Mannequin {
        login
      }
      ... on Organization {
        login
      }
      ... on Bot {
        login
      }
    }
  }
`

export function AssignedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: AssignedEventProps): JSX.Element {
  const {actor, createdAt, assignee, databaseId} = useFragment(AssignedEventFragment, queryRef)

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={PersonIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupAssignedEvent rollupGroup={rollupGroup} />
        ) : (
          <AddedAssigneesRendering
            queryRefs={[queryRef]}
            selfAssigned={actor?.login === assignee?.login}
            rollup={false}
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const AddedAssigneesRendering = ({
  queryRefs,
  selfAssigned,
  rollup,
}: {
  queryRefs: AssignedEvent$key[]
  selfAssigned: boolean
  rollup: boolean
}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {!selfAssigned && `${LABELS.timeline.assigned} `}
      {queryRefs.map((queryRef, index) => (
        <Fragment key={index}>
          <InternalAddedAssigneesRendering
            queryRef={queryRef}
            rollup={rollup}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedAssigneesRendering = ({
  queryRef,
  rollup,
  first,
  last,
}: Pick<AssignedEventProps, 'queryRef'> & {
  rollup: boolean
  first: boolean
  last: boolean
}) => {
  const {assignee, actor} = useFragment(AssignedEventFragment, queryRef)

  if (!assignee?.login) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        marginRight: 0,
        '&:last-of-type': {
          marginRight: 1,
        },
      }}
    >
      {actor?.login === assignee?.login && !rollup ? (
        <Text sx={{mr: 1}}>{LABELS.timeline.selfAssignedThis}</Text>
      ) : (
        <>
          {!first && !last && <Box sx={{mr: 1}}>,</Box>}
          {!first && last && <Box sx={{mr: 1, ml: 1}}>{LABELS.timeline.and}</Box>}
          <Assignee assigneeLogin={assignee?.login} />
        </>
      )}
    </Box>
  )
}

try{ AssignedEvent.displayName ||= 'AssignedEvent' } catch {}
try{ AddedAssigneesRendering.displayName ||= 'AddedAssigneesRendering' } catch {}
try{ InternalAddedAssigneesRendering.displayName ||= 'InternalAddedAssigneesRendering' } catch {}